import { useCallback, useMemo, useState } from 'react'
import ReactFlow, {
  Background, BackgroundVariant, useViewport,
  type Edge, type Node, type NodeChange, type XYPosition,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { cn } from '@/lib/utils'
import WorkflowStepNode from './WorkflowStepNode'
import LiveCardNode from './LiveCardNode'
import CanvasControls, { CANVAS_MAX_ZOOM, CANVAS_MIN_ZOOM } from './CanvasControls'

/**
 * tldraw grid parity — the board canvas (tldraw 3.9, `isGridMode: true`) and
 * this React Flow canvas must read as ONE product surface. The lattice is
 * pinned in SCREEN space to tldraw's zoom-1 rendering (what a board shows
 * when opened), because the workflow canvas fit-view lands at zoom < 1 and
 * flow-space dots would compress into a denser, darker field than the board:
 *  - distances: minor dots every 10 screen px, major every 40 (tldraw
 *    gridSize 10, step 4). RF scales `gap` by zoom, so gap = px / zoom pins
 *    it; panning still shifts the lattice with the canvas.
 *  - dot radius 1px fixed (tldraw's `r={1}`): RF's radius = size × zoom / 2,
 *    so size = 2 / zoom cancels the zoom scaling.
 *  - opacity: tldraw's zoom-1 fade — minors at modulate(1, [0.7, 2.5]) = 1/6,
 *    majors at 1. (Steps 16/64 coincide with the 4-lattice; two layers
 *    reproduce the visual exactly.)
 *  - colors live in index.css (.workflow-canvas-surface / .workflow-grid-dots)
 *    via the shared --canvas-workspace-* variables — the SAME values the
 *    board's .board-workspace-v2 tldraw overrides consume (the app restyles
 *    tldraw's stock grid, so parity targets those overrides, not tldraw's
 *    built-in theme tokens).
 */
const TLDRAW_GRID_SIZE = 10
const TLDRAW_MINOR_DOT_OPACITY = 1 / 6
// Referentially stable so React Flow never sees a fresh options object.
const RF_PRO_OPTIONS = { hideAttribution: true } as const

function TldrawParityGrid() {
  const { zoom } = useViewport()
  return (
    <>
      <Background
        id="tlgrid-minor"
        variant={BackgroundVariant.Dots}
        gap={TLDRAW_GRID_SIZE / (zoom || 1)}
        size={2 / (zoom || 1)}
        className="workflow-grid-dots"
        style={{ opacity: TLDRAW_MINOR_DOT_OPACITY }}
      />
      <Background
        id="tlgrid-major"
        variant={BackgroundVariant.Dots}
        gap={(4 * TLDRAW_GRID_SIZE) / (zoom || 1)}
        size={2 / (zoom || 1)}
        className="workflow-grid-dots"
      />
    </>
  )
}

/**
 * "Generating" card (Figma "3. Create Workflow" — Generating screens): a small
 * node-sized card floating over the canvas — header "Generating", body = the
 * Intuigence logo + orange "Generating Workflow" — NOT a full-screen curtain,
 * so the canvas (and any nodes that already exist) stays visible behind it.
 */
export function CanvasStatusOverlay({
  generatingLabel,
  generatingBodyLabel,
}: {
  generatingLabel?: string
  /** Translated body line — falls back to the header so no locale mixes languages. */
  generatingBodyLabel?: string
}) {
  if (!generatingLabel) return null
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <div
        role="status"
        aria-label={generatingLabel}
        className="w-[272px] overflow-hidden rounded-lg border border-border bg-background shadow-sm dark:bg-dark-card"
      >
        <div className="border-b border-border/70 px-3 py-2.5 text-sm font-semibold text-foreground">
          {generatingLabel}
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5">
          <img src="/images/intuai-logo-icon.svg" alt="" aria-hidden className="size-4 shrink-0" />
          <span className="animate-pulse text-sm font-medium text-orange-600 dark:text-orange-500">
            {generatingBodyLabel ?? generatingLabel}
          </span>
        </div>
      </div>
    </div>
  )
}

const defaultEdgeOptions = {
  type: 'smoothstep',
  style: { stroke: '#cbd5e1', strokeWidth: 1.5 },
}

interface WorkflowCanvasProps {
  nodes?: Node[]
  edges?: Edge[]
  /** Center empty-state card ("Let's build your workflow") when there are no nodes. */
  emptyState?: React.ReactNode
  /** When set, grays out the canvas and shows the "Generating workflow…" state. */
  generatingLabel?: string
  generatingBodyLabel?: string
  /**
   * "AI is working" treatment (Figma "5. Run Workflow"): a soft primary-color
   * inner glow around the whole canvas while the run is executing.
   */
  runningGlow?: boolean
  onNodeClick?: (data: unknown) => void
  /**
   * Fits the view again when this changes. React Flow fits only once, at mount: a graph that
   * arrives after the mount (a run loaded by id, a plan the engine proposes, a re-plan with more
   * steps) sat unfitted at the top-left. The page keys this on what it draws (plan and step
   * count), never on a refresh of the same graph, so a viewport the user panned is kept.
   */
  fitKey?: string
}

/**
 * The workflow canvas (read-only): dotted background, snake-laid node cards,
 * bottom-left zoom controls, and the running-glow frame. Authoring happens via
 * generation (chat/settings), not direct canvas edits.
 */
export default function WorkflowCanvas({
  nodes,
  edges,
  emptyState,
  generatingLabel,
  generatingBodyLabel,
  runningGlow = false,
  onNodeClick,
  fitKey,
}: WorkflowCanvasProps) {
  const nodeTypes = useMemo(() => ({ workflowStep: WorkflowStepNode, liveCard: LiveCardNode }), [])
  // Where the user dragged a node to (in memory only). The graph is controlled, so a drag has to
  // be fed back as a position; nodes no one dragged keep the layout's position.
  const [dragged, setDragged] = useState<Record<string, XYPosition>>({})
  const onNodesChange = useCallback((changes: NodeChange[]) => {
    const moves = changes.flatMap((c) => (c.type === 'position' && c.position ? [[c.id, c.position] as const] : []))
    if (moves.length) setDragged((d) => ({ ...d, ...Object.fromEntries(moves) }))
  }, [])
  const renderedNodes = useMemo(
    () => (nodes ?? []).map((n) => (dragged[n.id] ? { ...n, position: dragged[n.id] } : n)),
    [nodes, dragged]
  )
  const renderedEdges = useMemo(() => edges ?? [], [edges])

  // Inline tool chips on a step node click through to the tool-call detail. We
  // route them via the same onNodeClick the canvas already uses, tagged as a
  // tool selection, so the existing dialog wiring handles it.
  const nodesWithHandlers = useMemo(
    () =>
      onNodeClick
        ? renderedNodes.map((node) => ({
            ...node,
            data: {
              ...node.data,
              onSelectTool: (toolCall: unknown) =>
                onNodeClick({
                  kind: 'tool',
                  toolCall,
                  step: (node.data as { step?: unknown })?.step,
                }),
            },
          }))
        : renderedNodes,
    [renderedNodes, onNodeClick]
  )

  return (
    <div className="relative h-full">
      <ReactFlow
        key={fitKey}
        nodes={nodesWithHandlers}
        edges={renderedEdges}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        // Fit never zooms past 1:1 (tiny graphs stay readable); the fit scale is
        // what CanvasControls calibrates as 100% (FS-562 fit-baseline model).
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        // Wider native range than the 10%–200% display window so the stepped
        // zoom stays reachable for tiny/huge fit baselines (see CanvasControls).
        minZoom={CANVAS_MIN_ZOOM}
        maxZoom={CANVAS_MAX_ZOOM}
        // Scroll/trackpad pans the canvas (intuitive for a tall graph); zoom is via
        // the Controls buttons or ctrl/⌘+scroll. Drag also pans.
        panOnScroll
        zoomOnScroll={false}
        panOnDrag
        nodesDraggable={false}
        onNodesChange={onNodesChange}
        nodesConnectable={false}
        onNodeClick={onNodeClick ? (_, node) => onNodeClick(node.data) : undefined}
        // Drops the "React Flow" attribution chip bottom-right — it reads as
        // a third-party watermark inside the product canvas.
        proOptions={RF_PRO_OPTIONS}
        className="workflow-canvas-surface"
      >
        <TldrawParityGrid />
        <CanvasControls />
      </ReactFlow>

      {/* Primary-color inner glow while the AI runs (pointer-transparent frame). */}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-10 transition-opacity duration-500',
          runningGlow ? 'opacity-100' : 'opacity-0'
        )}
        style={{
          boxShadow:
            'inset 0 0 42px 6px rgba(234, 88, 12, 0.28), inset 0 0 12px 2px rgba(234, 88, 12, 0.18)',
        }}
      />

      <CanvasStatusOverlay
        generatingLabel={generatingLabel}
        generatingBodyLabel={generatingBodyLabel}
      />
      {emptyState && !generatingLabel && renderedNodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
          {emptyState}
        </div>
      )}
    </div>
  )
}
